/**
 * MemoCreateComposeOverride — the CRM create-override for `ahoy_note` and
 * `ahoy_task` (see createOverrides.tsx).
 *
 * Note creation no longer uses a dedicated modal; the single create/edit
 * surface is the global floating note compose panel (MemoComposeContext).
 * This headless adapter plugs the compose panel into the generic CRM create
 * flow (note-list "+", pipeline-stage "+"): when the create flow "opens" the
 * override, we forward the caller's defaults to `openMemoCompose()` and
 * immediately release the create flow's open state (the compose panel manages
 * its own visibility). It renders nothing.
 */

import { useEffect } from 'react';

import { useMemoCompose } from '@/contexts/MemoComposeContext';

import type { CrmCreateOverrideProps } from '../../create/createOverrides';

export function MemoCreateComposeOverride({
  entityTypeName,
  isOpen,
  onClose,
  defaults,
}: CrmCreateOverrideProps) {
  const { openMemoCompose } = useMemoCompose();

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    // Create the type this override was registered for (note vs task), not a
    // type inferred from the presence of a stage — a task created from an
    // empty column stays an ahoy_task.
    openMemoCompose({
      entityType: entityTypeName,
      stageEntityId: defaults?.stageEntityId,
      linkedEntities: defaults?.meetingEntityId
        ? [
            {
              entityId: defaults.meetingEntityId,
              entityTypeName: 'ahoy_meeting',
            },
          ]
        : undefined,
    });
    // Release the generic create flow's open state — the compose panel owns
    // visibility from here. Guarded by `isOpen` so this fires once per open.
    onClose();
  }, [
    entityTypeName,
    isOpen,
    openMemoCompose,
    onClose,
    defaults?.stageEntityId,
    defaults?.meetingEntityId,
  ]);

  return null;
}
